import React, { Fragment } from 'react';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import Link from 'app/common/components/Link';
import useVersionMigration from 'app/developer-v3/hooks/useVersionMigration';
import UserMigration, {
  MIGRATION_EMAIL_TYPES,
  MIGRATION_TYPES,
  MigrationType,
  MigrationEmailType,
} from 'app/developer-v3/components/UserMigration';

import Description from '../Description';
import useVersionUserCounts from './useVersionUserCounts';

export type Options =
  | {
      email: string;
      emailType: MigrationEmailType;
    }
  | {
      percentage: number;
    };

type Props = {
  initialFromVersion: string;
  integrationId: ID;
  isLoading: boolean;
};

function Start({ initialFromVersion, integrationId, isLoading }: Props) {
  const [fromVersion, setFromVersion] = React.useState(initialFromVersion);
  const [toVersion, setToVersion] = React.useState('');
  const [type, setType] = React.useState<MigrationType>(
    MIGRATION_TYPES.percentage
  );
  const [percentage, setPercentage] = React.useState(0);
  const [email, setEmail] = React.useState('');
  const [emailType, setEmailType] = React.useState<MigrationEmailType>(
    MIGRATION_EMAIL_TYPES.user
  );

  const { versions } = useVersionUserCounts();
  const { migrate } = useVersionMigration(integrationId);

  const onClickMigrate = () => {
    migrate(
      fromVersion,
      toVersion,
      type === MIGRATION_TYPES.percentage
        ? { percentage }
        : { email, emailType }
    );
  };

  return (
    <Fragment>
      <Description>
        Migrate user Zaps from the source to the destination version. You can
        migrate a specific user, a percentage of users, or all users at once.
      </Description>
      <Description>
        Make sure there are no breaking changes introduced for a successful
        migration. If there are issues, deprecate the source version instead to
        notify users.{' '}
        <Link
          href="https://docs.zapier.com/platform/manage/deprecate"
          target="_blank"
        >
          Learn more
        </Link>
        .
      </Description>
      <UserMigration
        email={email}
        emailType={emailType}
        fromVersion={fromVersion}
        numUsers={
          // this will never fail to find something, but flow is being a pain
          (versions.find(v => v.version === fromVersion) || {}).userCount
        }
        onChangeEmail={setEmail}
        onChangeEmailType={setEmailType}
        onChangePercentage={setPercentage}
        onChangeType={setType}
        onSetFromVersion={({ value }) => setFromVersion(value)}
        onSetToVersion={({ value }) => setToVersion(value)}
        percentage={percentage}
        toVersion={toVersion}
        type={type}
        versions={versions.map(v => v.version)}
      />
      <AsyncButton
        aria-label="Migrate Zaps"
        variant="primary"
        isDisabled={
          !toVersion ||
          (type === MIGRATION_TYPES.percentage && percentage === 0) ||
          (type === MIGRATION_TYPES.email && !email)
        }
        isWorking={isLoading}
        onPress={onClickMigrate}
        type="submit"
      >
        Migrate
      </AsyncButton>
    </Fragment>
  );
}

export default Start;
